import { Fragment, useCallback, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';
import {
  extractVersionFromFilename,
  mergeInstallersForVersion,
  platformFromFilename,
  sha256File,
} from './releaseMetadata';

const CHANNELS = ['stable', 'beta'];

export default function Releases() {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [releases, setReleases] = useState([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [pageError, setPageError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [copiedInstaller, setCopiedInstaller] = useState('');

  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [channel, setChannel] = useState('stable');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileMetadata, setFileMetadata] = useState(null);
  const [publishError, setPublishError] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishStage, setPublishStage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    let isActive = true;

    async function loadOrganizations() {
      try {
        const snapshot = await getDocs(collection(getFirestore(), 'organizations'));
        const organizationList = snapshot.docs
          .map((organizationDoc) => ({
            id: organizationDoc.id,
            ...organizationDoc.data(),
          }))
          .sort((left, right) => (
            left.name || left.id
          ).localeCompare(right.name || right.id));

        if (isActive) {
          setOrganizations(organizationList);
          setSelectedOrganizationId((currentId) => currentId || organizationList[0]?.id || '');
        }
      } catch (error) {
        console.error('Failed to load organizations:', error);
        if (isActive) {
          setPageError('Failed to load organizations. Refresh the page and try again.');
        }
      } finally {
        if (isActive) {
          setOrganizationsLoading(false);
        }
      }
    }

    loadOrganizations();
    return () => {
      isActive = false;
    };
  }, []);

  const loadReleases = useCallback(async (organizationId) => {
    if (!organizationId) {
      setReleases([]);
      return;
    }

    try {
      setReleasesLoading(true);
      setPageError('');
      const snapshot = await getDocs(
        collection(getFirestore(), 'organizations', organizationId, 'app_releases'),
      );
      const channelPriority = { stable: 0, beta: 1 };
      const releaseList = snapshot.docs
        .map((releaseDoc) => ({ id: releaseDoc.id, ...releaseDoc.data() }))
        .sort((left, right) => (
          (channelPriority[left.id] ?? 2) - (channelPriority[right.id] ?? 2)
          || left.id.localeCompare(right.id)
        ));

      setReleases(releaseList);
    } catch (error) {
      console.error('Failed to load releases:', error);
      setPageError('Failed to load releases for this organization.');
      setReleases([]);
    } finally {
      setReleasesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReleases(selectedOrganizationId);
  }, [loadReleases, selectedOrganizationId]);

  const selectedOrganization = organizations.find(
    (organization) => organization.id === selectedOrganizationId,
  );
  const existingChannelRelease = releases.find((release) => release.id === channel);
  const publishingNewVersion = Boolean(
    fileMetadata
    && existingChannelRelease
    && existingChannelRelease.version !== fileMetadata.version,
  );
  const publishingCurrentVersion = Boolean(
    fileMetadata
    && existingChannelRelease
    && existingChannelRelease.version === fileMetadata.version,
  );

  function openPublishDialog() {
    setChannel('stable');
    setReleaseNotes('');
    setSelectedFile(null);
    setFileMetadata(null);
    setPublishError('');
    setUploadProgress(0);
    setPublishStage('');
    setIsPublishOpen(true);
  }

  function closePublishDialog() {
    if (!publishing) {
      setIsPublishOpen(false);
    }
  }

  function handleFileChange(event) {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setFileMetadata(null);
    setPublishError('');

    if (!file) {
      return;
    }

    try {
      setFileMetadata({
        version: extractVersionFromFilename(file.name),
        platform: platformFromFilename(file.name),
      });
    } catch (error) {
      setPublishError(error.message);
    }
  }

  async function handleCopyUrl(url, installerKey) {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedInstaller(installerKey);
      window.setTimeout(() => setCopiedInstaller(''), 2000);
    } catch (error) {
      console.error('Failed to copy download URL:', error);
      setPageError('Could not copy the URL automatically. Select it in the field and copy it manually.');
    }
  }

  async function handlePublish() {
    if (!selectedOrganizationId || !selectedFile || !fileMetadata) {
      setPublishError('Select a valid installer before publishing.');
      return;
    }

    setPublishing(true);
    setPublishError('');
    setSuccessMessage('');
    setUploadProgress(0);

    try {
      const version = extractVersionFromFilename(selectedFile.name);
      const platform = platformFromFilename(selectedFile.name);
      const db = getFirestore();
      const releaseRef = doc(
        db,
        'organizations',
        selectedOrganizationId,
        'app_releases',
        channel,
      );

      setPublishStage('Checking the current release');
      const existingSnapshot = await getDoc(releaseRef);
      const existingRelease = existingSnapshot.exists() ? existingSnapshot.data() : null;

      setPublishStage('Computing SHA-256');
      const sha256 = await sha256File(selectedFile);
      const contentType = selectedFile.type
        || (platform === 'windows' ? 'application/x-msi' : 'application/octet-stream');

      setPublishStage('Uploading installer');
      const storageRef = ref(
        getStorage(),
        `releases/${channel}/${version}/${selectedFile.name}`,
      );
      const uploadTask = uploadBytesResumable(storageRef, selectedFile, { contentType });

      await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = snapshot.totalBytes === 0
              ? 0
              : Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setUploadProgress(progress);
          },
          reject,
          resolve,
        );
      });

      const url = await getDownloadURL(uploadTask.snapshot.ref);
      const installer = {
        url,
        sha256,
        signature: null,
        size: selectedFile.size,
        content_type: contentType,
      };

      setPublishStage('Saving release');
      await setDoc(releaseRef, {
        version,
        channel,
        release_notes: releaseNotes,
        installers: mergeInstallersForVersion(existingRelease, version, platform, installer),
        organization_id: selectedOrganizationId,
        updated_at: serverTimestamp(),
      });

      await loadReleases(selectedOrganizationId);
      setSuccessMessage(`${channel} ${version} was published for ${selectedOrganization?.name || selectedOrganizationId}.`);
      setIsPublishOpen(false);
    } catch (error) {
      console.error('Failed to publish release:', error);
      setPublishError(error.message || 'Failed to publish the release. Try again.');
    } finally {
      setPublishing(false);
      setPublishStage('');
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-end sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">App Releases</h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-700">
            Publish staged desktop app releases by organization and channel.
          </p>
        </div>
        <div className="mt-5 flex flex-col gap-3 sm:ml-8 sm:mt-0 sm:flex-row sm:items-end">
          <div>
            <label htmlFor="organization" className="block text-sm font-medium text-gray-900">
              Organization
            </label>
            <select
              id="organization"
              value={selectedOrganizationId}
              onChange={(event) => {
                setSelectedOrganizationId(event.target.value);
                setSuccessMessage('');
              }}
              disabled={organizationsLoading || organizations.length === 0}
              className="mt-1 block w-full min-w-[16rem] rounded-md border-gray-300 bg-white py-2 pl-3 pr-10 text-sm text-gray-900 shadow-sm focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
            >
              {organizations.length === 0 && (
                <option value="">No organizations available</option>
              )}
              {organizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name || organization.id}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={openPublishDialog}
            disabled={!selectedOrganizationId || organizationsLoading}
            className="inline-flex items-center justify-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
          >
            <ArrowUpTrayIcon className="h-5 w-5" aria-hidden="true" />
            Publish release
          </button>
        </div>
      </div>

      {pageError && (
        <div role="alert" className="mt-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-800">
          {pageError}
        </div>
      )}
      {successMessage && (
        <div role="status" className="mt-6 rounded-md border border-green-200 bg-green-50 p-4 text-sm font-medium text-green-800">
          {successMessage}
        </div>
      )}

      <section aria-labelledby="current-releases-heading" className="mt-8">
        <div className="border-b border-gray-200 pb-4">
          <h2 id="current-releases-heading" className="text-base font-semibold text-gray-900">
            Current channel releases
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Each channel points devices at one version; installers are grouped by platform.
          </p>
        </div>

        {releasesLoading ? (
          <div className="flex items-center justify-center py-16" role="status">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-600" />
            <span className="sr-only">Loading releases</span>
          </div>
        ) : releases.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm font-medium text-gray-900">No releases published</p>
            <p className="mt-1 text-sm text-gray-500">
              Publish the first stable or beta installer for this organization.
            </p>
          </div>
        ) : (
          <div className="mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Channel</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Version and notes</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Installers</th>
                    <th scope="col" className="py-3.5 pl-3 pr-4 text-left text-sm font-semibold text-gray-900 sm:pr-6">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {releases.map((release) => {
                    const updatedAt = release.updated_at?.toDate
                      ? release.updated_at.toDate().toLocaleString()
                      : 'Not available';
                    const installers = Object.entries(release.installers || {});

                    return (
                      <tr key={release.id} className="align-top">
                        <td className="whitespace-nowrap py-5 pl-4 pr-3 sm:pl-6">
                          <span className={`inline-flex rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset ${
                            release.id === 'stable'
                              ? 'bg-green-50 text-green-700 ring-green-600/20'
                              : 'bg-amber-50 text-amber-800 ring-amber-600/20'
                          }`}>
                            {release.id}
                          </span>
                        </td>
                        <td className="max-w-xs px-3 py-5 text-sm text-gray-700">
                          <p className="font-mono text-sm font-semibold text-gray-900">{release.version || 'Unknown'}</p>
                          <p className="mt-2 whitespace-pre-wrap">{release.release_notes || 'No release notes.'}</p>
                        </td>
                        <td className="min-w-[24rem] px-3 py-5">
                          {installers.length === 0 ? (
                            <p className="text-sm text-gray-500">No installers</p>
                          ) : (
                            <div className="space-y-5">
                              {installers.map(([platform, installer]) => {
                                const installerKey = `${release.id}-${platform}`;
                                return (
                                  <div key={platform}>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                      <span className="text-sm font-semibold capitalize text-gray-900">{platform}</span>
                                      <span className="text-xs text-gray-500">
                                        {typeof installer.size === 'number'
                                          ? `${(installer.size / (1024 * 1024)).toFixed(1)} MB`
                                          : 'Size unavailable'}
                                      </span>
                                      <span className="font-mono text-xs text-gray-500" title={installer.sha256 || ''}>
                                        SHA-256 {installer.sha256 ? `${installer.sha256.slice(0, 12)}…` : 'unavailable'}
                                      </span>
                                    </div>
                                    {installer.url ? (
                                      <div className="mt-2 flex max-w-2xl gap-2">
                                        <input
                                          type="text"
                                          readOnly
                                          aria-label={`${platform} download URL`}
                                          value={installer.url}
                                          onFocus={(event) => event.target.select()}
                                          className="block min-w-0 flex-1 rounded-md border-gray-300 bg-white py-1.5 text-xs text-gray-700 shadow-sm focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => handleCopyUrl(installer.url, installerKey)}
                                          className="inline-flex shrink-0 items-center gap-x-1 rounded-md bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                        >
                                          {copiedInstaller === installerKey ? (
                                            <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                          ) : (
                                            <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                                          )}
                                          {copiedInstaller === installerKey ? 'Copied' : 'Copy'}
                                        </button>
                                        <a
                                          href={installer.url}
                                          target="_blank"
                                          rel="noreferrer"
                                          aria-label={`Open ${platform} download URL`}
                                          className="inline-flex items-center rounded-md bg-gray-800 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-800"
                                        >
                                          <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
                                        </a>
                                      </div>
                                    ) : (
                                      <p className="mt-2 text-sm font-medium text-red-700">Download URL unavailable</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap py-5 pl-3 pr-4 text-sm text-gray-500 sm:pr-6">{updatedAt}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      <Transition.Root show={isPublishOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closePublishDialog}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/60 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-2xl transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:p-6">
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button
                      type="button"
                      onClick={closePublishDialog}
                      disabled={publishing}
                      className="rounded-md bg-white p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-300"
                    >
                      <span className="sr-only">Close publish dialog</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <Dialog.Title as="h2" className="pr-10 text-lg font-semibold text-gray-900">
                    Publish app release
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-600">
                    Version and platform are derived from the installer filename and cannot be edited.
                  </p>

                  <div className="mt-6 grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-900">Organization</label>
                      <div className="mt-2 rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-800 ring-1 ring-inset ring-gray-300">
                        {selectedOrganization?.name || selectedOrganizationId}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="release-channel" className="block text-sm font-medium text-gray-900">Channel</label>
                      <select
                        id="release-channel"
                        value={channel}
                        onChange={(event) => setChannel(event.target.value)}
                        disabled={publishing}
                        className="mt-2 block w-full rounded-md border-gray-300 bg-white py-2 pl-3 pr-10 text-sm text-gray-900 shadow-sm focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                      >
                        {CHANNELS.map((availableChannel) => (
                          <option key={availableChannel} value={availableChannel}>
                            {availableChannel.charAt(0).toUpperCase() + availableChannel.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-5">
                    <label htmlFor="installer-file" className="block text-sm font-medium text-gray-900">
                      Installer file
                    </label>
                    <input
                      id="installer-file"
                      type="file"
                      accept=".msi,.AppImage"
                      onChange={handleFileChange}
                      disabled={publishing}
                      className="mt-2 block w-full text-sm text-gray-600 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
                    />
                  </div>

                  {fileMetadata && selectedFile && (
                    <div className="mt-4 rounded-md border border-indigo-200 bg-indigo-50 p-4">
                      <p className="text-sm font-semibold text-indigo-900">Installer metadata</p>
                      <dl className="mt-2 grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <dt className="text-indigo-700">Parsed version</dt>
                          <dd className="mt-0.5 font-mono font-semibold text-indigo-950">{fileMetadata.version}</dd>
                        </div>
                        <div>
                          <dt className="text-indigo-700">Platform</dt>
                          <dd className="mt-0.5 font-semibold capitalize text-indigo-950">{fileMetadata.platform}</dd>
                        </div>
                      </dl>
                    </div>
                  )}

                  {publishingNewVersion && (
                    <div role="alert" className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                      <p className="font-semibold">This replaces {channel} {existingChannelRelease.version}.</p>
                      <p className="mt-1">
                        Its {Object.keys(existingChannelRelease.installers || {}).join(' and ') || 'existing'} installer entries will be dropped. Only the new {fileMetadata.platform} installer will belong to {fileMetadata.version}.
                      </p>
                    </div>
                  )}
                  {publishingCurrentVersion && (
                    <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-900">
                      <p className="font-semibold">Adding to {channel} {fileMetadata.version}.</p>
                      <p className="mt-1">
                        The {fileMetadata.platform} entry will be replaced; installers for other platforms will be preserved.
                      </p>
                    </div>
                  )}

                  <div className="mt-5">
                    <label htmlFor="release-notes" className="block text-sm font-medium text-gray-900">
                      Release notes
                    </label>
                    <textarea
                      id="release-notes"
                      rows="4"
                      value={releaseNotes}
                      onChange={(event) => setReleaseNotes(event.target.value)}
                      disabled={publishing}
                      placeholder="Optional operator-facing summary"
                      className="mt-2 block w-full rounded-md border-gray-300 bg-white text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                    />
                  </div>

                  {publishing && (
                    <div className="mt-5" role="status">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-gray-900">{publishStage}</span>
                        <span className="font-mono text-gray-600">
                          {publishStage === 'Uploading installer' ? `${uploadProgress}%` : ''}
                        </span>
                      </div>
                      <div
                        className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200"
                        role="progressbar"
                        aria-label="Release publish progress"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        aria-valuenow={publishStage === 'Uploading installer' ? uploadProgress : undefined}
                      >
                        <div
                          className={`h-full rounded-full bg-indigo-600 transition-all duration-200 ${
                            publishStage === 'Uploading installer' ? '' : 'animate-pulse'
                          }`}
                          style={{ width: publishStage === 'Uploading installer' ? `${uploadProgress}%` : '35%' }}
                        />
                      </div>
                      {publishStage === 'Computing SHA-256' && (
                        <p className="mt-2 text-xs text-gray-500">
                          Large installers are read into memory while their SHA-256 is computed.
                        </p>
                      )}
                    </div>
                  )}

                  {publishError && (
                    <div role="alert" className="mt-5 rounded-md border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800">
                      {publishError}
                    </div>
                  )}

                  <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={closePublishDialog}
                      disabled={publishing}
                      className="inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handlePublish}
                      disabled={publishing || !fileMetadata}
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
                    >
                      {publishing ? 'Publishing…' : `Publish to ${channel}`}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
